import type { IdeaNode, Section } from '@/types';

export interface IdeaCluster {
  key: string;
  label: string;
  sectionId?: string;
  nodes: IdeaNode[];
}

export interface SectionTreeRow {
  section: Section;
  depth: number;
  rootId: string;
}

export interface IdeaPlacement {
  primarySectionId?: string;
  primarySectionLabel: string;
  primarySectionPathLabel: string;
  primaryRootSectionId?: string;
  primaryRootSectionLabel: string;
  extraSectionIds: string[];
}

export interface IdeaBoardGroup {
  key: string;
  label: string;
  sectionId?: string;
  nodes: IdeaNode[];
}

export function buildSectionById(sections: Section[]) {
  return new Map(sections.map((section) => [section.id, section] as const));
}

export function getRootSection(sectionId: string, sectionById: Map<string, Section>): Section | undefined {
  let current = sectionById.get(sectionId);
  while (current?.parentSectionId) {
    current = sectionById.get(current.parentSectionId);
  }
  return current;
}

export function buildSectionPathLabel(sectionId: string, sectionById: Map<string, Section>): string {
  const parts: string[] = [];
  let current = sectionById.get(sectionId);
  while (current) {
    parts.unshift(current.title || current.name || 'Seccion');
    current = current.parentSectionId ? sectionById.get(current.parentSectionId) : undefined;
  }
  return parts.join(' > ');
}

export function buildSectionTreeRows(sections: Section[]): SectionTreeRow[] {
  const byParent = new Map<string | undefined, Section[]>()
  sections.forEach((section) => {
    const key = section.parentSectionId
    const siblings = byParent.get(key) ?? []
    siblings.push(section)
    byParent.set(key, siblings)
  })

  const rows: SectionTreeRow[] = []
  const walk = (parentId: string | undefined, depth: number, rootId?: string) => {
    const siblings = [...(byParent.get(parentId) ?? [])].sort((left, right) => {
      const leftOrder = left.order ?? 0
      const rightOrder = right.order ?? 0
      if (leftOrder !== rightOrder) return leftOrder - rightOrder
      return (left.title || left.name || '').localeCompare(right.title || right.name || '')
    })

    siblings.forEach((section) => {
      const nextRootId = rootId ?? section.id
      rows.push({ section, depth, rootId: nextRootId })
      walk(section.id, depth + 1, nextRootId)
    })
  }

  walk(undefined, 0)
  return rows
}

export function listRootSections(sections: Section[]): Section[] {
  return sections
    .filter((section) => !section.parentSectionId)
    .sort((left, right) => {
      const leftOrder = left.order ?? 0
      const rightOrder = right.order ?? 0
      if (leftOrder !== rightOrder) return leftOrder - rightOrder
      return (left.title || left.name || '').localeCompare(right.title || right.name || '')
    })
}

export function buildChapterSectionRows(sections: Section[], chapterId?: string): SectionTreeRow[] {
  if (!chapterId) return []
  return buildSectionTreeRows(sections).filter((row) => row.rootId === chapterId && row.depth > 0)
}

export function resolveIdeaPlacement(node: IdeaNode, sections: Section[]): IdeaPlacement {
  const sectionById = buildSectionById(sections)
  const validLinkedSectionIds = node.links
    .map((link) => link.sectionId)
    .filter((sectionId, index, array) => array.indexOf(sectionId) === index)
    .filter((sectionId) => sectionById.has(sectionId))

  const inferredPrimarySectionId = validLinkedSectionIds[0]
  const hasExplicitPrimarySectionId = Object.prototype.hasOwnProperty.call(node, 'primarySectionId')
  const primarySectionId = node.primarySectionId && sectionById.has(node.primarySectionId)
    ? node.primarySectionId
    : (hasExplicitPrimarySectionId ? undefined : inferredPrimarySectionId)
  const primarySection = primarySectionId ? sectionById.get(primarySectionId) : undefined
  const primaryRootSection = primarySection
    ? (getRootSection(primarySection.id, sectionById) ?? primarySection)
    : undefined
  const extraSectionIds = validLinkedSectionIds
    .filter((sectionId) => sectionId !== primarySection?.id)
    .filter((sectionId, index, array) => array.indexOf(sectionId) === index)

  return {
    primarySectionId: primarySection?.id,
    primarySectionLabel: primarySection?.title || primarySection?.name || '',
    primarySectionPathLabel: primarySection ? buildSectionPathLabel(primarySection.id, sectionById) : '',
    primaryRootSectionId: primaryRootSection?.id,
    primaryRootSectionLabel: primaryRootSection?.title || primaryRootSection?.name || '',
    extraSectionIds,
  }
}

function resolveClusterKey(node: IdeaNode, sectionById: Map<string, Section>) {
  const primarySectionId = node.primarySectionId || node.links[0]?.sectionId;
  if (!primarySectionId) {
    return { key: 'unlinked', label: 'Ideas sin enlace' };
  }
  const rootSection = getRootSection(primarySectionId, sectionById) ?? sectionById.get(primarySectionId);
  if (!rootSection) {
    return { key: `missing:${primarySectionId}`, label: 'Ideas sin ancla visible' };
  }
  return {
    key: rootSection.id,
    label: rootSection.title || rootSection.name || 'Sección',
    sectionId: rootSection.id,
  };
}

export function buildIdeaBoardGroups(nodes: IdeaNode[], sections: Section[]): IdeaBoardGroup[] {
  const rootSections = listRootSections(sections)
  const rootSectionById = new Map(rootSections.map((section) => [section.id, section] as const))
  const groupMap = new Map<string, IdeaBoardGroup>()

  nodes.forEach((node) => {
    const placement = resolveIdeaPlacement(node, sections)
    const key = placement.primaryRootSectionId ?? 'unplaced'
    const label = placement.primaryRootSectionId
      ? (rootSectionById.get(placement.primaryRootSectionId)?.title || rootSectionById.get(placement.primaryRootSectionId)?.name || 'Capítulo')
      : 'Sin capítulo todavía'

    const existing = groupMap.get(key) ?? {
      key,
      label,
      sectionId: placement.primaryRootSectionId,
      nodes: [],
    }
    existing.nodes.push(node)
    groupMap.set(key, existing)
  })

  const groups = [...groupMap.values()].map((group) => ({
    ...group,
    nodes: [...group.nodes].sort((left, right) => {
      const leftPlacement = resolveIdeaPlacement(left, sections)
      const rightPlacement = resolveIdeaPlacement(right, sections)
      const leftCovered = leftPlacement.extraSectionIds.length
      const rightCovered = rightPlacement.extraSectionIds.length
      if (leftCovered !== rightCovered) return rightCovered - leftCovered
      return left.title.localeCompare(right.title)
    }),
  }))

  return groups.sort((left, right) => {
    if (left.key === 'unplaced') return 1
    if (right.key === 'unplaced') return -1
    const leftOrder = rootSectionById.get(left.key)?.order ?? Number.MAX_SAFE_INTEGER
    const rightOrder = rootSectionById.get(right.key)?.order ?? Number.MAX_SAFE_INTEGER
    if (leftOrder !== rightOrder) return leftOrder - rightOrder
    return left.label.localeCompare(right.label)
  })
}

export function buildIdeaClusters(nodes: IdeaNode[], sections: Section[]): IdeaCluster[] {
  const sectionById = buildSectionById(sections);
  const clusterMap = new Map<string, IdeaCluster>();

  nodes.forEach((node) => {
    const clusterMeta = resolveClusterKey(node, sectionById);
    const cluster = clusterMap.get(clusterMeta.key) ?? {
      key: clusterMeta.key,
      label: clusterMeta.label,
      sectionId: clusterMeta.sectionId,
      nodes: [],
    };
    cluster.nodes.push(node);
    clusterMap.set(clusterMeta.key, cluster);
  });

  return [...clusterMap.values()]
    .map((cluster) => ({
      ...cluster,
      nodes: [...cluster.nodes].sort((left, right) => {
        if (left.type !== right.type) return left.type.localeCompare(right.type);
        return left.title.localeCompare(right.title);
      }),
    }))
    .sort((left, right) => {
      if (left.key === 'unlinked') return 1;
      if (right.key === 'unlinked') return -1;
      return left.label.localeCompare(right.label);
    });
}
